import React, { JSXElementConstructor, ReactElement } from "react";
import { Tooltip } from "bootstrap";
import { renderToStaticMarkup } from "react-dom/server";
import { Cast, Switch } from "../../utils/Utils";

// Variable global para rastrear el tooltip activo y prevenir múltiples tooltips abiertos
let activeTooltipInstance: Tooltip | null = null;

const TooltipComponent = (
  props: React.PropsWithChildren<{
    title: string | JSX.Element | false;
    placement?: string;
    className?: string;
    html?: boolean;
    onShow?: () => void;
    onInserted?: () => void;
    hideDelay?: number;
    animation?: boolean;
  }>
) => {
  const elementsRef = React.useRef<(HTMLSpanElement | null)[]>([]);

  React.useEffect(() => {
    if (props.title === false) {
      return;
    }
    const options: Partial<Tooltip.Options> = {
      customClass:
        "tooltipIntiza" +
        (typeof props.title === "string" ? "" : " bigTooltip"),
      delay: { show: 100, hide: 0 },
      title:
        typeof props.title === "string"
          ? props.title
          : renderToStaticMarkup(props.title),
      html: typeof props.title !== "string" || Boolean(props.html),
      trigger: "manual",
      container: "body",
      animation: props.animation ?? true
    };
    const tooltips = elementsRef.current
      .filter((x) => x !== null)
      .map((span) => Tooltip.getOrCreateInstance(span!, options));
    const elements = elementsRef.current.filter((x) => x !== null);

    // Mantener referencias a los handlers para poder limpiarlos
    const cleanupHandlers: Array<() => void> = [];
    const hideTimeouts: Map<number, NodeJS.Timeout> = new Map();
    const showTimeouts: Map<number, NodeJS.Timeout> = new Map();

    // Agregar eventos para mantener el tooltip visible cuando el mouse está sobre él
    elements.forEach((element, index) => {
      const tooltipInstance = tooltips[index];
      let tooltipElement: HTMLElement | null = null;
      let isMouseOverTooltip = false;
      let isMouseOverElement = false;

      const scheduleHide = () => {
        const existingTimeout = hideTimeouts.get(index);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }
        const timeout = setTimeout(() => {
          if (!isMouseOverTooltip && !isMouseOverElement) {
            tooltipInstance.hide();
          }
          hideTimeouts.delete(index);
        }, props?.hideDelay || 500);
        hideTimeouts.set(index, timeout);
      };

      // Mantener el tooltip visible cuando el mouse está sobre el elemento
      const handleElementMouseEnter = () => {
        isMouseOverElement = true;
        // Cancelar cualquier timeout de hide pendiente
        const existingHideTimeout = hideTimeouts.get(index);
        if (existingHideTimeout) {
          clearTimeout(existingHideTimeout);
          hideTimeouts.delete(index);
        }
        // Cancelar cualquier timeout de show pendiente
        const existingShowTimeout = showTimeouts.get(index);
        if (existingShowTimeout) {
          clearTimeout(existingShowTimeout);
          showTimeouts.delete(index);
        }
        // Programar el show después de 500ms
        const showTimeout = setTimeout(() => {
          // Si hay otro tooltip activo, cerrarlo primero
          if (
            activeTooltipInstance &&
            activeTooltipInstance !== tooltipInstance
          ) {
            try {
              activeTooltipInstance.hide();
            } catch (e) {
              // Ignorar errores si el tooltip ya fue eliminado
            }
            activeTooltipInstance = null;
          }
          // Verificar que el elemento aún existe en el DOM
          if (element && document.contains(element)) {
            tooltipInstance.show();
          }
          showTimeouts.delete(index);
        }, 500);
        showTimeouts.set(index, showTimeout);
      };

      const handleElementMouseLeave = () => {
        isMouseOverElement = false;
        // Cancelar el timeout de show si el mouse sale antes de los 500ms
        const existingShowTimeout = showTimeouts.get(index);
        if (existingShowTimeout) {
          clearTimeout(existingShowTimeout);
          showTimeouts.delete(index);
        }
        if (!isMouseOverTooltip) {
          scheduleHide();
        }
      };

      element!.addEventListener("mouseenter", handleElementMouseEnter);
      element!.addEventListener("mouseleave", handleElementMouseLeave);

      const handleShown = () => {
        // Registrar este tooltip como activo
        activeTooltipInstance = tooltipInstance;
        // Buscar el elemento del tooltip después de que se muestre
        setTimeout(() => {
          // Verificar que el elemento aún existe en el DOM
          if (!element || !document.contains(element)) {
            return;
          }
          // Buscar todos los tooltips y encontrar el que corresponde a este elemento
          const allTooltips = document.querySelectorAll(
            ".tooltipIntiza, .tooltipIntiza.bigTooltip"
          );
          allTooltips.forEach((tt) => {
            const tip = tt as HTMLElement;
            // Verificar si este tooltip está relacionado con nuestro elemento
            const rect = element!.getBoundingClientRect();
            const tipRect = tip.getBoundingClientRect();
            // Si el tooltip está cerca del elemento, probablemente es el correcto
            const isNearby =
              Math.abs(tipRect.top - rect.bottom) < 150 ||
              Math.abs(tipRect.bottom - rect.top) < 150 ||
              Math.abs(tipRect.left - rect.right) < 150 ||
              Math.abs(tipRect.right - rect.left) < 150;

            if (isNearby && !tooltipElement) {
              tooltipElement = tip;

              // Cuando el mouse entra al tooltip, mantenerlo visible
              const handleTooltipMouseEnter = () => {
                isMouseOverTooltip = true;
                const existingTimeout = hideTimeouts.get(index);
                if (existingTimeout) {
                  clearTimeout(existingTimeout);
                  hideTimeouts.delete(index);
                }
              };

              // Cuando el mouse sale del tooltip, programar el ocultamiento
              const handleTooltipMouseLeave = () => {
                isMouseOverTooltip = false;
                if (!isMouseOverElement) {
                  scheduleHide();
                }
              };

              tip.addEventListener("mouseenter", handleTooltipMouseEnter);
              tip.addEventListener("mouseleave", handleTooltipMouseLeave);

              // Guardar referencias para limpiar
              cleanupHandlers.push(() => {
                tip.removeEventListener("mouseenter", handleTooltipMouseEnter);
                tip.removeEventListener("mouseleave", handleTooltipMouseLeave);
              });
            }
          });
        }, 100);
      };

      const handleHidden = () => {
        // Limpiar la referencia del tooltip activo si este es el que se está ocultando
        if (activeTooltipInstance === tooltipInstance) {
          activeTooltipInstance = null;
        }
        tooltipElement = null;
        isMouseOverTooltip = false;
        isMouseOverElement = false;
        const existingTimeout = hideTimeouts.get(index);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          hideTimeouts.delete(index);
        }
      };

      element!.addEventListener("shown.bs.tooltip", handleShown);
      element!.addEventListener("hidden.bs.tooltip", handleHidden);

      cleanupHandlers.push(() => {
        element!.removeEventListener("shown.bs.tooltip", handleShown);
        element!.removeEventListener("hidden.bs.tooltip", handleHidden);
        element!.removeEventListener("mouseenter", handleElementMouseEnter);
        element!.removeEventListener("mouseleave", handleElementMouseLeave);
        const existingHideTimeout = hideTimeouts.get(index);
        if (existingHideTimeout) {
          clearTimeout(existingHideTimeout);
          hideTimeouts.delete(index);
        }
        const existingShowTimeout = showTimeouts.get(index);
        if (existingShowTimeout) {
          clearTimeout(existingShowTimeout);
          showTimeouts.delete(index);
        }
      });
    });

    if (props.onShow && elementsRef.current) {
      elements.forEach((x) =>
        x!.addEventListener("shown.bs.tooltip", props.onShow!)
      );
    }
    if (props.onInserted && elementsRef.current) {
      elements.forEach((x) =>
        x!.addEventListener("inserted.bs.tooltip", props.onInserted!)
      );
    }
    return () => {
      // Limpiar todos los handlers
      cleanupHandlers.forEach((handler) => handler());

      // Limpiar todos los timeouts de hide
      hideTimeouts.forEach((timeout) => clearTimeout(timeout));
      hideTimeouts.clear();

      // Limpiar todos los timeouts de show
      showTimeouts.forEach((timeout) => clearTimeout(timeout));
      showTimeouts.clear();

      // Ocultar y limpiar todos los tooltips antes de dispose
      tooltips.forEach((tooltip, index) => {
        try {
          tooltip.dispose();
        } catch (e) {
          // Ignorar errores si el tooltip ya fue eliminado o el elemento no existe
        }
        // Limpiar la referencia global si este tooltip era el activo
        if (activeTooltipInstance === tooltip) {
          activeTooltipInstance = null;
        }
      });
      if (props.onShow) {
        elements.forEach((x) => {
          try {
            x!.removeEventListener("shown.bs.tooltip", props.onShow!);
          } catch (e) {
            // Ignorar errores si el elemento ya fue eliminado
          }
        });
      }
      if (props.onInserted) {
        elements.forEach((x) => {
          try {
            x!.removeEventListener("inserted.bs.tooltip", props.onInserted!);
          } catch (e) {
            // Ignorar errores si el elemento ya fue eliminado
          }
        });
      }
    };
  }, [
    props.html,
    props.onShow,
    props.onInserted,
    props.title,
    props?.hideDelay,
    props.animation
  ]);
  if (!props.title) {
    return <>{props.children}</>;
  }
  if (typeof props.children === "string") {
    return (
      <span
        ref={(ref) => (elementsRef.current[0] = ref)}
        data-bs-placement={props.placement}
        className={props.className}
      >
        {props.children}
      </span>
    );
  }
  const items = React.Children.map(props.children, (child, index) => {
    const assingRef = (ref: HTMLElement) => (elementsRef.current[index] = ref);
    return (
      Switch(
        [typeof child === "string", <span ref={assingRef}>{child}</span>],
        [
          Boolean(
            child &&
            typeof child === "object" &&
            "type" in child &&
            typeof child.type === "function"
          ),
          <span
            ref={(ref) => (elementsRef.current[0] = ref)}
            data-bs-placement={props.placement}
            className={props.className}
          >
            {props.children}
          </span>
        ]
      ) ??
      React.cloneElement(
        Cast<ReactElement<any, string | JSXElementConstructor<any>>>(child),
        {
          ref: assingRef
        }
      )
    );
  });
  return <>{items}</>;
};

export default TooltipComponent;
